'use client';

import React, { createContext, useCallback, useMemo, useRef, useState, useContext } from 'react';
import dynamic from 'next/dynamic';
import { IAlertProps } from '@/components/general/Alert';
import Toast, { ToastTone } from '@/components/design-system/Toast';
import toast from 'react-hot-toast';

const Alert = dynamic(() => import('@/components/general/Alert'), {
    ssr: false,
    loading: () => null
});

export type AlertContextType = {
    show: (data: IAlertProps) => void;
    hide: () => void;
};

const AlertContext = createContext<AlertContextType>({
    show: () => {},
    hide: () => {}
});

/**
 * The old alert was a 48px-headline dialog over a blurred backdrop, thrown up
 * for things as small as "You've already placed a bid of $20.00." The Toasts
 * master (385:4759) is where those belong, so `show` now raises a toast and
 * the dialog is kept only for the two shapes a toast cannot carry.
 *
 * The signature is unchanged on purpose: all 27 call sites keep calling
 * `show({ text, description, ... })` and get the new treatment for free.
 */

/** The `animation` field was already carrying tone; it just said it in Lottie. */
const TONE: Record<NonNullable<IAlertProps['animation']>, ToastTone> = {
    AlertAnimation: 'alert',
    WarningAnimation: 'error',
    SuccessAnimation: 'success',
    LoadingAnimation: 'loading'
};

/**
 * One alert is not a notification and must not become a toast:
 *
 *   confirmAction    A yes/no decision with two buttons — "change the pickup
 *                    method?", "accept instant buy?". The master's toast has
 *                    one action slot, and a decision that dismisses itself
 *                    after six seconds is not a decision.
 *
 * LoadingAnimation used to keep the dialog too, as a blocking spinner over
 * `stripe.confirmPayment`. Its callers already refuse a second click while
 * they are loading, so nothing needed blocking, and the dialog was left on
 * screen whenever a caller followed it with a toast instead of `hide()`.
 */
const needsDialog = (data: IAlertProps) => !!data.confirmAction;

export const AlertProvider = ({ children }: { children: React.ReactNode }) => {
    const [alert, setAlert] = useState<IAlertProps | null>(null);
    const [showAlert, setShowAlert] = useState(false);
    const lastToast = useRef<string | null>(null);
    /*
     * A loading toast never times out, so it is tracked apart from
     * `lastToast` and closed by whatever comes next: the result's toast, or
     * `hide()` on failure.
     */
    const loadingToast = useRef<string | null>(null);

    const dismissLoading = useCallback(() => {
        if (loadingToast.current) {
            toast.dismiss(loadingToast.current);
            loadingToast.current = null;
        }
    }, []);

    const hide = useCallback(() => {
        setShowAlert(false);
        // clear the payload shortly after close to allow exit animations
        setTimeout(() => setAlert(null), 0);
        if (lastToast.current) {
            toast.dismiss(lastToast.current);
            lastToast.current = null;
        }
        dismissLoading();
    }, [dismissLoading]);

    const show = useCallback((data: IAlertProps) => {
        if (needsDialog(data)) {
            setAlert(data);
            setShowAlert(true);
            return;
        }

        dismissLoading();

        if (data.animation === 'LoadingAnimation') {
            loadingToast.current = toast.custom(
                <Toast tone="loading" title={data.text} description={data.description} />,
                { duration: Infinity }
            );
            return;
        }

        lastToast.current = toast.custom((t) => (
            <Toast
                tone={data.animation ? TONE[data.animation] : 'info'}
                title={data.text}
                description={data.description && (
                    /*
                     * Descriptions carry links — "add an address", "add a
                     * payment method". The dialog closed itself when one was
                     * clicked so the user was not left staring at a backdrop
                     * over the page they had just navigated to; the toast
                     * dismisses for the same reason.
                     */
                    <span
                        onClick={(e) => {
                            if ((e.target as Node)?.nodeName === 'A') {
                                toast.dismiss(t.id);
                            }
                        }}
                    >
                        {data.description}
                    </span>
                )}
                action={data.showButton && data.buttonText
                    ? {
                        label: data.buttonText,
                        onClick: async () => {
                            try {
                                await data.action?.();
                            // eslint-disable-next-line no-empty
                            } catch (error) {}
                            toast.dismiss(t.id);
                        }
                    }
                    : undefined}
                onDismiss={() => toast.dismiss(t.id)}
            />
        ));
    }, [dismissLoading]);

    const value = useMemo(() => ({ show, hide }), [show, hide]);

    return (
        <AlertContext.Provider value={value}>
            {children}
            {showAlert && (
                <Alert alert={alert} showAlert={showAlert} onHide={hide} />
            )}
        </AlertContext.Provider>
    );
};

export const useAlert = () => useContext(AlertContext);
